import type { UpdateRoutingLoadScopeSettingsRequest } from '@nadoutong/contracts';
import {
  useDeleteRoutingLoadScopeSettings,
  usePutRoutingLoadScopeSettings,
  useRoutingLoadScopeSettings,
  useRoutingLoadSettings,
} from '../../api/useRoutingLoad.ts';
import { coerceErrorMessage, useToast } from '../Toast.tsx';
import { useT } from '../../i18n/index.ts';
import { Button, Panel } from '../../ui/index.ts';
import { RoutingLoadScopeEditor } from '../../pages/routing-load/RoutingLoadScopeEditor.tsx';
import type { ScopeKillSwitches } from '../../pages/routing-load/RoutingLoadScopeEditor.tsx';
import { scopeRowOf } from '../../pages/routing-load/loadScopeFields.ts';

export function SkuLoadScopePanel({
  skuId,
  namespaceSlug,
}: {
  readonly skuId: string;
  readonly namespaceSlug: string;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const settings = useRoutingLoadSettings();
  const scopeSettings = useRoutingLoadScopeSettings();
  const putScope = usePutRoutingLoadScopeSettings();
  const deleteScope = useDeleteRoutingLoadScopeSettings();
  const data = settings.data;
  const skuRow = scopeRowOf(scopeSettings.data?.items, 'sku', skuId);
  const namespaceRow = scopeRowOf(scopeSettings.data?.items, 'namespace', namespaceSlug);
  const saving = putScope.isPending || deleteScope.isPending;

  function mutatePatch(body: UpdateRoutingLoadScopeSettingsRequest): void {
    putScope.mutate(
      { kind: 'sku', scopeId: skuId, body },
      {
        onSuccess: () => toast.success(t('sku_detail.load_scope.toast_saved')),
        onError: (error) => toast.error(coerceErrorMessage(error)),
      },
    );
  }

  return (
    <Panel caption={t('sku_detail.load_scope.title')} padded testId="sku-load-scope-panel">
      <div className="flex flex-col gap-3">
        {data !== undefined && (data.l2_env_kill_switch || data.l3_env_kill_switch || data.l4_env_kill_switch) ? (
          <p
            className="m-0 rounded border border-warn-line bg-warn-dim/50 px-3 py-2 text-dense leading-5 text-warn"
            data-testid="sku-load-scope-kill"
          >
            {data.l2_env_kill_switch
              ? t('tenant_routing.load.settings.kill_switch.l2')
              : data.l3_env_kill_switch
                ? t('tenant_routing.load.settings.kill_switch.l3')
                : t('tenant_routing.load.settings.kill_switch.l4')}
          </p>
        ) : null}
        <p className="m-0 text-dense leading-5 text-paper-low">{t('sku_detail.load_scope.hint')}</p>
        {settings.isPending || scopeSettings.isPending || data === undefined ? (
          <p className="m-0 text-dense text-paper-low" data-testid="sku-load-scope-loading">
            {t('sku_detail.load_scope.loading')}
          </p>
        ) : (
          <RoutingLoadScopeEditor
            kind="sku"
            scopeId={skuId}
            skuValues={skuRow}
            namespaceValues={namespaceRow}
            globalEffective={data.effective}
            kill={killFromSettings(data)}
            saving={saving}
            onPatch={mutatePatch}
          />
        )}
        <div className="flex justify-end">
          <Button
            variant="ghost"
            disabled={saving || skuRow === null}
            onClick={() => {
              deleteScope.mutate(
                { kind: 'sku', scopeId: skuId },
                {
                  onSuccess: () => toast.success(t('sku_detail.load_scope.toast_cleared')),
                  onError: (error) => toast.error(coerceErrorMessage(error)),
                },
              );
            }}
            data-testid="sku-load-scope-restore"
          >
            {t('sku_detail.load_scope.restore')}
          </Button>
        </div>
      </div>
    </Panel>
  );
}

function killFromSettings(data: {
  readonly l2_env_kill_switch: boolean;
  readonly l3_env_kill_switch: boolean;
  readonly l4_env_kill_switch: boolean;
  readonly session_rehome_env_kill_switch: boolean;
}): ScopeKillSwitches {
  return {
    l2: data.l2_env_kill_switch,
    l3: data.l3_env_kill_switch,
    l4: data.l4_env_kill_switch,
    sessionRehome: data.session_rehome_env_kill_switch,
  };
}
